'use client';

import type { ChangeEvent } from 'react';
import React, { forwardRef, useId, useState } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { Caption, Text } from '@/design-system/components/Basic/Typography';
import { Time } from '@/utils/Time';
import { isValid, parse } from 'date-fns';
import { useTranslate } from '@tolgee/react';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const TIME_PLACEHOLDER_EN = 'HH:MM';
const TIME_PLACEHOLDER_GER = 'HH:MM Uhr';

export interface TimeInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'onError'
> {
  label?: string;
  error?: string;
  /** Value as Time; undefined or null for empty */
  value?: Time | null;
  onChange?: (time: Time | null) => void;
  onError?: (error: string | null) => void;
  className?: string;
  inputClassName?: string;
  suppressBuiltInError?: boolean;
}

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      label,
      error,
      value,
      onChange,
      className,
      inputClassName,
      id,
      required,
      disabled,
      onBlur,
      onError,
      suppressBuiltInError,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `time-input-${generatedId.replace(/:/g, '-')}`;

    const { t } = useTranslate();
    const locale = useSelectedLanguage();
    const TIME_PLACEHOLDER = locale === 'en' ? TIME_PLACEHOLDER_EN : TIME_PLACEHOLDER_GER;

    const [timeValue, setTimeValue] = useState(() => (value ? value.toDisplayString() : ''));
    const [errorMessage, setErrorMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const selectionRef = React.useRef<{ start: number; end: number } | null>(null);

    const handleBeforeInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      selectionRef.current = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      };
    };

    // Live validation & live update later if needed.
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const inputType = (e.nativeEvent as InputEvent).inputType;
      if (hasError) {
        setHasError(false);
        setErrorMessage('');
      }
      const prev = selectionRef.current;
      const hadSelection = prev != null && prev.start !== prev.end;
      const cursorAtEnd = prev != null && prev.start === value.length - 1;
      if (hadSelection || !cursorAtEnd) {
        setTimeValue(value);
        selectionRef.current = null;
        return;
      }

      if (inputType?.startsWith('delete')) {
        setTimeValue(value);
        return;
      }

      const formatted = Time.formatInput(value);
      setTimeValue(formatted);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (timeValue.trim() === '') {
        setHasError(false);
        setErrorMessage('');
        onError?.(null);
        return;
      }
      const parsedTime = parse(timeValue, 'HH:mm', new Date());
      const isValidTime = isValid(parsedTime);
      if (!isValidTime) {
        setHasError(true);
        setErrorMessage(t('common.invalid_time'));
        onError?.(t('common.invalid_time'));
        return;
      }
      // send valid time
      const parsed = Time.fromDisplayString(timeValue);
      onChange?.(parsed ?? null);
      onBlur?.(e);
      onError?.(null);
    };

    return (
      <div
        className={twMerge(
          classNames('ds-input-wrapper flex flex-col gap-3 min-h-25 max-w-ds-40', className),
        )}
      >
        {label && (
          <Text as="label" size="s" weight="semibold" htmlFor={inputId}>
            {label}
            {required && ' *'}
          </Text>
        )}
        <div className="container-gradient-border rounded-xl">
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={!!error || !!hasError}
            aria-describedby={error || hasError ? `${inputId}-error` : undefined}
            placeholder={TIME_PLACEHOLDER}
            value={timeValue}
            onChange={handleChange}
            onBeforeInput={handleBeforeInput}
            onBlur={handleBlur}
            maxLength={5}
            className={classNames(
              'ds-input-trigger-base input-border-gradient min-h-ds-11 px-3.5 py-2.5',
              inputClassName,
            )}
            {...rest}
          />
        </div>
        {!suppressBuiltInError && (error || errorMessage) && (
          <Caption as="p" id={`${inputId}-error`} className="text-ds-text-feedback-error">
            {error || errorMessage}
          </Caption>
        )}
      </div>
    );
  },
);

TimeInput.displayName = 'TimeInput';

export default TimeInput;
export { Time };
